import { useState, useEffect } from 'react';
import { injectTheme, getSubdomain, redirectToHostname} from '../utils/whitelabel';
import { validateURL, validateWhitelabel } from '../utils/helpers';
import { DEFAULT_THEME_SUBDOMAIN } from '../constants/whiteLabel';
import { DEV_SUBDOMAIN_PREFIX, STAGING_SUBDOMAIN_PREFIX } from '../constants/appEnv';

// Map of appName to the subdomain segment used in URLs for that app.
// This segment is stripped from the subdomain since app identity is
// already sent via the app_name query parameter.
const APP_NAME_TO_SUBDOMAIN_SEGMENT = { 'kyc-portal': 'kyc' };

// Remove the app-specific segment from the subdomain by position.
// The app segment sits right before the env prefix (dev/staging)
// or is the last segment in prod.
// Returns UNBRANDED when no client subdomain remains after stripping
// (e.g. "kyc.dev" → strip "kyc" → "dev" → UNBRANDED).
const stripAppSegment = (subdomain, segment) => {
  const parts = subdomain.split('.');
  const lastIdx = parts.length - 1;
  const hasEnvSuffix =
    parts[lastIdx] === DEV_SUBDOMAIN_PREFIX ||
    parts[lastIdx] === STAGING_SUBDOMAIN_PREFIX;
  const appSegmentIdx = hasEnvSuffix ? lastIdx - 1 : lastIdx;

  if (appSegmentIdx >= 0 && parts[appSegmentIdx] === segment) {
    parts.splice(appSegmentIdx, 1);
  }

  const result = parts.filter(Boolean).join('.');

  if (!result || result === DEV_SUBDOMAIN_PREFIX || result === STAGING_SUBDOMAIN_PREFIX) {
    return DEFAULT_THEME_SUBDOMAIN;
  }

  return result;
};

export default (appName) => {
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState({});
  const [localization, setLocalization] = useState(null);
  const [callCenterBanner, setCallCenterBanner] = useState(null);
  const [e6MigrationBanner, setE6MigrationBanner] = useState(null);
  const [limitUpdateBanner, setLimitUpdateBanner] = useState(null);
  const [error, setError] = useState(null);

  const {hostname} = window.location;

  const rawSubdomain = getSubdomain(hostname);
  const segmentToStrip = APP_NAME_TO_SUBDOMAIN_SEGMENT[appName];
  const subdomain = segmentToStrip
    ? stripAppSegment(rawSubdomain, segmentToStrip)
    : rawSubdomain;
  // If the appName is passed into the function then append it
  // as query parameters to the URL
  const appNameQuery = appName ? `app_name=${appName}` : '';

  useEffect(() => {
    async function fetchTheme() {
      try {
        const subDomain = validateWhitelabel(subdomain);
        const url = validateURL(`/api/v1/white_label/${subDomain}?${appNameQuery}`);
        const res = await fetch(url);
        if (res.ok) {
          const json = await res.json();
          const themeData = injectTheme({ ...json.data.theme, processor: json.data.processor, copyright_company_name: json.data.copyright_company_name });
          setTheme(themeData);
          setLocalization(json.data.language_configuration);
          setCallCenterBanner(json.data.call_center_banner);
          setE6MigrationBanner(json.data.e6_migration);
          setLimitUpdateBanner(json.data.hrb_limit_update_banner);
          setLoading(false);
        } else {
          // Unable to retrieve a theme based on the current subdomain
          const result = redirectToHostname();
          setError(result);
          setLoading(false);
        }
      } catch (e) {
        const result = redirectToHostname();
        setError(result);
        setLoading(false);
      }
    }

    fetchTheme();
  }, []);

  return {
    loading,
    theme,
    localization,
    callCenterBanner,
    e6MigrationBanner,
    limitUpdateBanner,
    error,
  };
};
